import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CompanyPaymentSettingDto,
  CompanyPaymentSettingListQueryDto,
} from "@/dto/InstallmentDto";
import {
  getInstallmentSettings,
  deleteInstallmentSetting,
} from "@/apis/installment";
import { toast } from "react-hot-toast";

interface UseInstallmentSettingListReturn {
  settings: CompanyPaymentSettingDto[];
  loading: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  searchParams: CompanyPaymentSettingListQueryDto;
  handlePageChange: (newPage: number) => void;
  handlePageSizeChange: (newPageSize: number) => void;
  handleSearch: (searchForm: any) => void;
  handleGoToCreate: () => void;
  handleGoToEdit: (settingId: number) => void;
  handleDelete: (settingId: number) => void;
  handleRefresh: () => void;
}

const useInstallmentSettingList = (): UseInstallmentSettingListReturn => {
  const navigate = useNavigate();

  const [settings, setSettings] = useState<CompanyPaymentSettingDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] =
    useState<CompanyPaymentSettingListQueryDto>({
      page: 1,
      limit: 10,
      search: "",
      allowInstallment: "",
    });

  // 분할결제 설정 목록 조회
  const fetchSettings = useCallback(
    async (params: CompanyPaymentSettingListQueryDto) => {
      try {
        setLoading(true);
        const response = await getInstallmentSettings(params);

        setSettings(response.list);
        setTotalCount(response.count);
        setPage(params.page || 1);
        setPageSize(params.limit || 10);
      } catch (error) {
        console.error("분할결제 설정 목록 조회 실패:", error);
        toast.error("분할결제 설정 목록을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // 초기 데이터 로드
  useEffect(() => {
    fetchSettings(searchParams);
  }, [fetchSettings, searchParams]);

  // 페이지 변경
  const handlePageChange = useCallback(
    (newPage: number) => {
      const newSearchParams = { ...searchParams, page: newPage };
      setSearchParams(newSearchParams);
    },
    [searchParams]
  );

  // 페이지 크기 변경
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      const newSearchParams = {
        ...searchParams,
        page: 1,
        limit: newPageSize,
      };
      setSearchParams(newSearchParams);
    },
    [searchParams]
  );

  // 검색
  const handleSearch = useCallback(
    (searchForm: { search: string; allowInstallment: string }) => {
      const newSearchParams: CompanyPaymentSettingListQueryDto = {
        ...searchParams,
        page: 1,
        search: searchForm.search.trim() || undefined,
        allowInstallment: searchForm.allowInstallment || undefined,
      };
      setSearchParams(newSearchParams);
    },
    [searchParams]
  );

  // 새 설정 추가 페이지로 이동
  const handleGoToCreate = useCallback(() => {
    navigate("/installment-setting/create");
  }, [navigate]);

  // 설정 수정 페이지로 이동
  const handleGoToEdit = useCallback(
    (settingId: number) => {
      navigate(`/installment-setting/${settingId}`);
    },
    [navigate]
  );

  // 설정 삭제
  const handleDelete = useCallback(
    async (settingId: number) => {
      const confirmed = window.confirm(
        "이 분할결제 설정을 삭제하시겠습니까?\n삭제된 설정은 복구할 수 없습니다."
      );

      if (!confirmed) return;

      try {
        setLoading(true);
        await deleteInstallmentSetting(settingId);
        toast.success("분할결제 설정이 삭제되었습니다.");

        // 목록 새로고침
        await fetchSettings(searchParams);
      } catch (error) {
        console.error("분할결제 설정 삭제 실패:", error);
        toast.error("분할결제 설정 삭제에 실패했습니다.");
      } finally {
        setLoading(false);
      }
    },
    [fetchSettings, searchParams]
  );

  // 새로고침
  const handleRefresh = useCallback(() => {
    fetchSettings(searchParams);
  }, [fetchSettings, searchParams]);

  return {
    settings,
    loading,
    totalCount,
    page,
    pageSize,
    searchParams,
    handlePageChange,
    handlePageSizeChange,
    handleSearch,
    handleGoToCreate,
    handleGoToEdit,
    handleDelete,
    handleRefresh,
  };
};

export default useInstallmentSettingList;
